import { useState, useEffect, useRef } from "react";
import config from "../config";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

/* ─────────────────────────────────────────
   WEBSITE PREVIEW POPUP
   Shows an iframe preview of the supplier's website on logo hover.
   Falls back gracefully if the site blocks iframes.
───────────────────────────────────────────*/
function WebsitePreview({ url, name, visible, anchorRef }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);

  useEffect(() => {
    if (!visible || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const popupW = 320;
    const popupH = 210;
    let left = rect.left + rect.width / 2 - popupW / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - popupW - 12));
    const top = rect.top + scrollY - popupH - 14;
    setPos({ top, left });
  }, [visible, anchorRef]);

  if (!visible || !url) return null;

  return (
    <div
      ref={popupRef}
      className="wsp__popup"
      style={{ top: pos.top, left: pos.left }}
      aria-label={`Preview of ${name}`}
    >
      <div className="wsp__popup-header">
        <span className="wsp__popup-dot" style={{ background: "#ff5f57" }} />
        <span className="wsp__popup-dot" style={{ background: "#ffbd2e" }} />
        <span className="wsp__popup-dot" style={{ background: "#28ca41" }} />
        <span className="wsp__popup-url">{url.replace(/^https?:\/\//, "")}</span>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="wsp__popup-open"
          aria-label="Open website"
        >
          ↗
        </a>
      </div>
      <iframe
        src={url}
        title={`Preview: ${name}`}
        className="wsp__iframe"
        loading="lazy"
        sandbox="allow-scripts allow-same-origin"
      />
      <div className="wsp__popup-overlay" />
    </div>
  );
}

/* ─────────────────────────────────────────
   SINGLE SUPPLIER LOGO ITEM
───────────────────────────────────────────*/
function SupplierLogo({ supplier }) {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const logoUrl = `${UPLOADS_URL}/suppliers/${supplier.logo_filename}`;

  return (
    <div
      ref={ref}
      className="vst__logo-item"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={supplier.name}
    >
      <img
        src={logoUrl}
        alt={supplier.name}
        className="vst__logo-img"
        draggable="false"
      />
      {hovered && (
        <div className="vst__tooltip">{supplier.name}</div>
      )}
      <WebsitePreview
        url={supplier.website}
        name={supplier.name}
        visible={hovered}
        anchorRef={ref}
      />
    </div>
  );
}

/* ─────────────────────────────────────────
   LOGO TICKER (infinite seamless scroll)
───────────────────────────────────────────*/
function LogoTicker({ suppliers }) {
  const items = [...suppliers, ...suppliers, ...suppliers];

  return (
    <div className="vst__ticker-wrap">
      <div className="vst__ticker-track">
        {items.map((s, i) => (
          <SupplierLogo key={`${s.id}-${i}`} supplier={s} />
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   MAIN SECTION
───────────────────────────────────────────*/
function VideoSuppliersSection() {
  const [data, setData] = useState(null);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    fetchJSON(`${API_BASE}/video-suppliers.php`)
      .then(setData)
      .catch((err) => console.error("VideoSuppliers load error:", err));
  }, []);

  const video = data?.video || null;
  const suppliers = data?.suppliers || [];

  const isYouTube = video?.video_url?.includes("youtube") || video?.video_url?.includes("youtu.be");
  const isVimeo = video?.video_url?.includes("vimeo");
  const isDirectFile = video && !isYouTube && !isVimeo;

  function toYTEmbed(url) {
    const match = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
    if (!match) return url;
    const id = match[1];
    return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&rel=0&playsinline=1&enablejsapi=1`;
  }

  function toVimeoEmbed(url) {
    const match = url.match(/vimeo\.com\/(\d+)/);
    if (!match) return url;
    return `https://player.vimeo.com/video/${match[1]}?autoplay=1&muted=1&loop=1&background=1`;
  }

  const thumbnailUrl = video?.thumbnail_filename
    ? `${UPLOADS_URL}/videos/${video.thumbnail_filename}`
    : null;

  return (
    <section className="vst">
      {/* ── BACKGROUND VIDEO ── */}
      <div className="vst__bg">
        <div className="vst__overlay" />

        {isDirectFile && !videoError && (
          <video
            className="vst__video"
            autoPlay
            muted
            loop
            playsInline
            poster={thumbnailUrl || undefined}
            onError={() => setVideoError(true)}
          >
            <source src={video.video_url} type="video/mp4" />
          </video>
        )}

        {(isYouTube || isVimeo) && (
          <iframe
            className="vst__video vst__video--iframe"
            src={isYouTube ? toYTEmbed(video.video_url) : toVimeoEmbed(video.video_url)}
            title="Background video"
            allow="autoplay; muted; loop; fullscreen; picture-in-picture"
            allowFullScreen
            frameBorder="0"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        )}

        {(!video || videoError) && thumbnailUrl && (
          <img src={thumbnailUrl} alt="" className="vst__video vst__video--fallback" />
        )}
      </div>

      {/* ── FOREGROUND CONTENT ── */}
      <div className="vst__content">

        {/* LEFT: heading + CTA */}
        <div className="vst__left">
          <span className="vst__eyebrow">How It Works</span>
          <h2 className="vst__heading">
            COOL <em>COMFORT IN 4 SIMPLE </em> STEPS
          </h2>
          <p className="vst__sub">
            From your first call to enjoying a perfectly cooled space — our
            process is fast, transparent, and hassle-free.
          </p>
          <a href="/contact-us" className="vst__cta">
            BOOK A VISIT <span aria-hidden="true">→</span>
          </a>
        </div>

        {/* RIGHT: glass steps panel */}
        <div className="vst__steps-panel">
          {[
            {
              num: "01",
              title: "Schedule a Visit",
              desc: "Book a convenient appointment and let our certified technicians assess your cooling needs professionally.",
              icon: "cooler.svg",
            },
            {
              num: "02",
              title: "Get a Quote",
              desc: "Receive a transparent, competitive quote tailored to your air conditioning requirements and budget.",
              icon: "money.svg",
            },
            {
              num: "03",
              title: "Fix or Install",
              desc: "Our skilled team efficiently repairs, services, or installs your air conditioning system safely.",
              icon: "ac-repair.svg",
            },
            {
              num: "04",
              title: "Enjoy Cool Comfort",
              desc: "Relax in a perfectly cooled environment with reliable, energy-efficient air conditioning performance.",
              icon: "happy.svg",
            },
          ].map((step, i) => (
            <div className="vst__step" key={i}>
              <span className="vst__step-ghost" aria-hidden="true">.{step.num}</span>
              <div className="vst__step-icon">
                <img
                  src={`${UPLOADS_URL}/svg-icons/${step.icon}`}
                  alt=""
                  aria-hidden="true"
                />
              </div>
              <div className="vst__step-body">
                <h3 className="vst__step-title">{step.title.toUpperCase()}</h3>
                <p className="vst__step-desc">{step.desc}</p>
              </div>
              {i < 3 && <div className="vst__step-divider" aria-hidden="true" />}
            </div>
          ))}
        </div>

      </div>

      {/* ── LOGO TICKER BAR ── */}
      {suppliers.length > 0 && (
        <div className="vst__bar">
          <LogoTicker suppliers={suppliers} />
        </div>
      )}

      {/* ── STYLES ── */}
      <style>{`
        /* ─── SECTION SHELL ─── */
        .vst {
          position: relative;
          width: 100%;
          min-height: 520px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        /* ─── VIDEO BG ─── */
        .vst__bg {
          position: absolute;
          inset: 0;
          z-index: 0;
        }
        .vst__overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            160deg,
            rgba(1, 20, 60, 0.78) 0%,
            rgba(1, 61, 151, 0.60) 50%,
            rgba(0, 0, 0, 0.72) 100%
          );
          z-index: 1;
        }
        .vst__video {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        .vst__video--iframe {
          width: 100vw;
          height: 56.25vw;
          min-height: 100%;
          min-width: 177.78vh;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          border: none;
        }
        .vst__video--fallback {
          object-fit: cover;
        }

        /* ─── FOREGROUND CONTENT (two-column) ─── */
        .vst__content {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: 1280px;
          margin: 0 auto;
          padding: 70px 40px 130px;
          display: grid;
          grid-template-columns: 1.4fr 1fr;
          align-items: center;
          gap: 32px;
        }

        /* ── LEFT ── */
        .vst__left {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .vst__eyebrow {
          display: inline-block;
          background: rgba(255, 255, 255, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.4);
          color: #fff;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: .12em;
          text-transform: uppercase;
          padding: 6px 18px;
          border-radius: 999px;
          margin-bottom: 22px;
        }
        .vst__heading {
          font-size: clamp(26px, 3.5vw, 46px);
          font-weight: 900;
          color: #fff;
          line-height: 1.15;
          margin-bottom: 18px;
          letter-spacing: -.5px;
        }
        .vst__heading em {
          font-style: normal;
          color: #dfdf09;
        }
        .vst__sub {
          font-size: 16px;
          color: rgba(255,255,255,.75);
          line-height: 1.75;
          margin-bottom: 32px;
          max-width: 420px;
        }
        .vst__cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: #029add;
          color: #fff;
          font-size: 15px;
          font-weight: 700;
          padding: 14px 32px;
          border-radius: 8px;
          text-decoration: none;
          transition: background 0.2s, transform 0.2s;
          box-shadow: 0 8px 28px rgba(2,154,221,.4);
        }
        .vst__cta:hover {
          background: #0287c4;
          transform: translateY(-2px);
        }

        /* ── RIGHT: GLASS STEPS PANEL ── */
        .vst__steps-panel {
          background: #424244;
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 20px;
          padding: 4px 0;
          display: flex;
          flex-direction: column;
        }

        /* ── SINGLE STEP ── */
        .vst__step {
          position: relative;
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 14px 20px 14px 20px;
          transition: background 0.2s;
          overflow: hidden;
        }
        .vst__step::before {
          content: '';
          position: absolute;
          left: 0; top: 0; bottom: 0;
          width: 3px;
          background: #f76b1c;
          border-radius: 0 2px 2px 0;
          transform: scaleY(0);
          transform-origin: center;
          transition: transform 0.22s ease;
        }
        .vst__step:hover::before {
          transform: scaleY(1);
        }
        .vst__step:hover {
          background: rgba(247,107,28,0.08);
        }
        .vst__step-divider {
          position: absolute;
          bottom: 0; left: 20px; right: 20px;
          height: 1px;
          background: rgba(255,255,255,0.09);
        }

        .vst__step-ghost {
          position: absolute;
          right: 14px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 58px;
          font-weight: 900;
          color: rgba(223, 223, 9, 0.15);
          line-height: 1;
          pointer-events: none;
          user-select: none;
          font-family: 'Roboto', sans-serif;
          letter-spacing: -2px;
        }

        /* ICON */
        .vst__step-icon {
          flex-shrink: 0;
          width: 42px;
          height: 42px;
          border-radius: 10px;
          background: rgba(247,107,28,0.18);
          border: 1px solid rgba(247,107,28,0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 2px;
        }
        .vst__step-icon img {
          width: 22px;
          height: 22px;
          object-fit: contain;
          filter: brightness(0) invert(1);
        }

        /* BODY */
        .vst__step-body {
          flex: 1;
          min-width: 0;
        }
        .vst__step-title {
          font-size: 14px;
          font-weight: 800;
          color: #fff;
          margin-bottom: 4px;
          line-height: 1.25;
        }
        .vst__step-desc {
          font-size: 12px;
          color: rgba(255,255,255,0.62);
          line-height: 1.6;
          margin: 0;
        }

        /* ─── TICKER BAR ─── */
        .vst__bar {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 3;
          background: #fff;
          box-shadow: 0 -4px 30px rgba(0,0,0,.12);
          padding: 0;
          overflow: hidden;
        }
        .vst__ticker-wrap {
          overflow: hidden;
          width: 100%;
          padding: 14px 0;
          -webkit-mask-image: linear-gradient(
            to right,
            transparent 0%,
            #000 8%,
            #000 92%,
            transparent 100%
          );
          mask-image: linear-gradient(
            to right,
            transparent 0%,
            #000 8%,
            #000 92%,
            transparent 100%
          );
        }
        .vst__ticker-track {
          display: flex;
          align-items: center;
          gap: 0;
          width: max-content;
          animation: vst-scroll 38s linear infinite;
        }
        .vst__ticker-track:hover {
          animation-play-state: paused;
        }
        @keyframes vst-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }

        /* ─── LOGO ITEM ─── */
        .vst__logo-item {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px 36px;
          border-right: 1px solid #e8eaf0;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.2s;
        }
        .vst__logo-item:hover {
          background: #f7f9ff;
        }
        .vst__logo-img {
          height: 36px;
          width: auto;
          max-width: 140px;
          object-fit: contain;
          filter: grayscale(1) opacity(.65);
          transition: filter 0.25s;
          user-select: none;
          pointer-events: none;
        }
        .vst__logo-item:hover .vst__logo-img {
          filter: grayscale(0) opacity(1);
        }

        /* ─── TOOLTIP ─── */
        .vst__tooltip {
          position: absolute;
          bottom: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          background: #1a1a2e;
          color: #fff;
          font-size: 12px;
          font-weight: 600;
          padding: 5px 12px;
          border-radius: 6px;
          white-space: nowrap;
          pointer-events: none;
          z-index: 10;
          box-shadow: 0 4px 14px rgba(0,0,0,.25);
        }
        .vst__tooltip::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 5px solid transparent;
          border-top-color: #1a1a2e;
        }

        /* ─── WEBSITE PREVIEW POPUP ─── */
        .wsp__popup {
          position: fixed;
          z-index: 9999;
          width: 320px;
          height: 210px;
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0,0,0,.28), 0 0 0 1px rgba(0,0,0,.06);
          pointer-events: none;
          animation: wsp-in 0.18s ease;
        }
        @keyframes wsp-in {
          from { opacity: 0; transform: translateY(6px) scale(.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .wsp__popup-header {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #f0f0f0;
          padding: 7px 10px;
          border-bottom: 1px solid #e0e0e0;
        }
        .wsp__popup-dot {
          width: 10px; height: 10px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .wsp__popup-url {
          flex: 1;
          font-size: 11px;
          color: #444;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          background: #fff;
          border-radius: 4px;
          padding: 2px 8px;
          border: 1px solid #ddd;
        }
        .wsp__popup-open {
          font-size: 14px;
          color: #013d97;
          text-decoration: none;
          flex-shrink: 0;
          pointer-events: all;
          padding: 2px 4px;
        }
        .wsp__iframe {
          width: 200%;
          height: 200%;
          border: none;
          transform: scale(0.5);
          transform-origin: top left;
          pointer-events: none;
        }
        .wsp__popup-overlay {
          position: absolute;
          inset: 30px 0 0 0;
          z-index: 1;
        }

        /* ─── RESPONSIVE ─── */
        @media (max-width: 960px) {
          .vst__content {
            grid-template-columns: 1fr;
            padding: 60px 28px 120px;
            gap: 36px;
          }
          .vst__left { align-items: center; text-align: center; }
          .vst__sub { max-width: 100%; }
        }
        @media (max-width: 768px) {
          .vst { min-height: 440px; }
          .vst__content { padding: 50px 20px 110px; }
          .vst__logo-item { padding: 10px 20px; }
          .vst__logo-img { height: 26px; max-width: 110px; }
          .vst__step { padding: 18px 20px; gap: 14px; }
          .vst__step-divider { left: 20px; right: 20px; }
        }
        @media (max-width: 480px) {
          .vst { min-height: 400px; }
          .vst__bar {
            position: relative;
            bottom: auto; left: auto; right: auto;
          }
          .vst__content { padding: 40px 16px 32px; }
          .vst__ticker-wrap { padding: 10px 0; }
          .vst__logo-item { padding: 8px 16px; border-right: 1px solid #e8eaf0; }
          .vst__logo-img { height: 22px; max-width: 90px; filter: grayscale(0) opacity(1); }
          .vst__step-icon { width: 40px; height: 40px; border-radius: 10px; }
          .vst__step-icon img { width: 20px; height: 20px; }
          .vst__step-title { font-size: 14px; }
          .vst__step-desc { font-size: 12px; }
          .wsp__popup { display: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .vst__ticker-track { animation: none; }
        }
      `}</style>
    </section>
  );
}

export default VideoSuppliersSection;